import styled from '@emotion/styled';
import { FileReference } from '@kaizenplatform/belfast-entity';
import { Button, FormControl, FormHelperText, TextField, Typography } from '@material-ui/core';
import { ClassAttributes, createElement, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  File,
  Issue,
  IssueComment,
  Maybe,
  RequestItem,
  RequestState,
  User,
} from '../../../../../graphql/generated/graphql';
import useInputComment, { inputCommentLimit } from '../../../../../hooks/useInputComment';
import theme from '../../../../../theme';
import hmsToSeconds from '../../../../../utility/hmsToSeconds';
import { SPACING_REGULAR, SPACING_UNIT } from '../../../../constant/spacing';
import { flexibleLayoutContainer } from '../../../../styleUtility/flexibleLayout';
import { gridLayoutContainer, gridLayoutItem } from '../../../../styleUtility/gridLayout';
import AspectRatioFixed from '../../../primitive/AspectRatioFixed';
import _AvatarWithName from '../../../primitive/AvatarWithName';
import LfToBr from '../../../primitive/LfToBr';
import FileList, { FileListItem } from '../../FileList';
import MediaPlayer from '../../MediaPlayer';
import ModificationRequestIssueDetail from './ModificationRequestIssueDetail';

interface Props {
  creativeFile: Pick<File, 'mimeType' | 'url'>;
  hasSubmittedDeliverable: boolean;
  modificationRequestState: RequestState;
  bucketId: string;
  assets: Array<FileReference>;
  onRequestCreateModificationRequestIssue: (id: string, body: string) => void;
  onRequestCreateModificationRequestItemIssueComment: (
    id: string,
    body: string,
    bucketId: string,
    assetIds: string[],
  ) => void;
  onRequestResolveModificationRequestIssue: (id: string) => void;
  onRequestReopenModificationRequestIssue: (id: string) => void;
  item: Pick<RequestItem, 'id' | 'typeName' | 'metadata' | 'sender' | 'assetIds' | 'body'> & {
    issue?: Maybe<
      Pick<Issue, 'id' | 'resolved'> & {
        comments: (Pick<IssueComment, 'assetIds' | 'body' | 'createdAt'> & {
          user: Pick<User, 'name' | 'profileImageUrl'>;
        })[];
      }
    >;
  };
  index: number;
  className?: string;
}

const Item = ({
  creativeFile,
  hasSubmittedDeliverable,
  modificationRequestState,
  bucketId,
  assets,
  onRequestCreateModificationRequestIssue,
  onRequestCreateModificationRequestItemIssueComment,
  onRequestResolveModificationRequestIssue,
  onRequestReopenModificationRequestIssue,
  item,
  index,
  className,
}: ClassAttributes<HTMLElement> & Props) => {
  const duration = item.metadata['duration'];
  const sender = item.sender;

  const fileItems = item.assetIds.map((id) => assets.filter((file) => file.id === id)).flatMap((value) => value);

  const [isOpenForm, setOpenFormState] = useState(false);
  const [inputComment, commentHandler] = useInputComment('');

  const isDisabled = hasSubmittedDeliverable || !!item.issue || modificationRequestState === 'given_feedback';

  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef && videoRef.current && duration) {
      const videoEl = videoRef.current;
      const canPlayCallback = () => {
        if (videoEl.paused) {
          videoEl.currentTime = hmsToSeconds(duration);
        }
      };
      videoEl.addEventListener('canplay', () => canPlayCallback(), {
        once: true,
      });
      return videoEl.removeEventListener('canplay', canPlayCallback);
    }
  }, [duration]);

  return (
    <Root className={className}>
      <DeliverableScreen>
        <VideoOrImage aspectRatio={[4, 3]}>
          <MediaPlayer mimeType={creativeFile.mimeType} url={creativeFile.url} videoRef={videoRef} />
        </VideoOrImage>
      </DeliverableScreen>

      <Content>
        <Titles>
          <Title variant="subtitle1">
            <FormattedMessage id="modification_request.modification_request_item.title" />
            {(index + 1).toString()}
          </Title>
          {creativeFile.mimeType.startsWith('video/') || creativeFile.mimeType.startsWith('audio/') ? (
            <Duration
              color="primary"
              variant="body2"
              onClick={() => {
                if (videoRef && videoRef.current && duration) {
                  videoRef.current.currentTime = hmsToSeconds(duration);
                }
              }}
            >
              {duration}
            </Duration>
          ) : null}
          {sender.name ? <AvatarWithName name={sender.name} imageUrl={sender.profileImageUrl} /> : null}
        </Titles>

        {item.typeName ? <Type variant="subtitle1">{item.typeName}</Type> : null}

        <InputtedComment variant="subtitle1" component="p">
          <LfToBr text={item.body} />
        </InputtedComment>

        {fileItems ? (
          <FileList>
            {[
              ...fileItems.map((file) => (
                <FileListItem file={file} key={`modification-request-file-${file.id}`} isVisibleSecondary={false} />
              )),
            ]}
          </FileList>
        ) : null}

        {!isDisabled ? (
          !isOpenForm ? (
            <Button color="primary" size="small" variant="contained" onClick={() => setOpenFormState(true)}>
              <FormattedMessage id="modification_request.modification_request_item.issue_request" />
            </Button>
          ) : (
            <div>
              <CommentForm error={inputComment.error}>
                <Comment
                  label={<FormattedMessage id="modification_request.modification_request_item.input_comment.label" />}
                  value={inputComment.body}
                  variant="outlined"
                  rows="4"
                  rowsMax="8"
                  multiline
                  error={inputComment.error}
                  onChange={(e) => commentHandler.setComment(e.target.value)}
                />
                <CommentSummary>
                  <Typography variant="caption">
                    <FormattedMessage id="modification_request.modification_request_item.input_comment.sup" />
                  </Typography>
                  <InputCount>{`${inputComment.body.length}/${inputCommentLimit}`}</InputCount>
                </CommentSummary>
              </CommentForm>

              <ButtonArea>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => {
                    commentHandler.setComment('');
                    setOpenFormState(false);
                  }}
                >
                  <FormattedMessage id="modification_request.modification_request_item.input_comment.cancel" />
                </Button>

                <Submit
                  color="primary"
                  size="small"
                  variant="contained"
                  disabled={inputComment.body.trim() === '' || inputComment.error}
                  onClick={() => onRequestCreateModificationRequestIssue(item.id, inputComment.body)}
                >
                  <FormattedMessage id="modification_request.modification_request_item.input_comment.submit" />
                </Submit>
              </ButtonArea>
            </div>
          )
        ) : null}

        {item.issue ? (
          <ModificationRequestIssueDetail
            issue={item.issue}
            bucketId={bucketId}
            assets={assets}
            modificationRequestState={modificationRequestState}
            onRequestCreateModificationRequestItemIssueComment={onRequestCreateModificationRequestItemIssueComment}
            onRequestResolveModificationRequestIssue={onRequestResolveModificationRequestIssue}
            onRequestReopenModificationRequestIssue={onRequestReopenModificationRequestIssue}
          />
        ) : null}
      </Content>
    </Root>
  );
};

const Root = styled('div')(
  gridLayoutContainer({
    columns: ['35%', '65%'],
    rows: ['auto'],
    areas: [['deliverable', 'content']],
  }),
  {
    paddingTop: SPACING_REGULAR,
    borderTop: `1px ${theme.palette.grey['200']} solid`,
  },
);

const DeliverableScreen = styled('div')(gridLayoutItem('deliverable', { alignSelf: 'flex-start' }), {
  marginRight: SPACING_REGULAR,
  marginBottom: SPACING_REGULAR,
});

const VideoOrImage = styled(AspectRatioFixed)({
  marginBottom: SPACING_REGULAR,
});

const Content = styled('div')(gridLayoutItem('content', { alignSelf: 'flex-start' }), {
  marginRight: SPACING_REGULAR,
});

const Titles = styled('div')(flexibleLayoutContainer({ alignItems: 'center' }), {
  marginBottom: SPACING_REGULAR,
});

const Type = styled(Typography)({
  marginBottom: SPACING_UNIT,
});

const Title = styled(Typography)({ fontWeight: 'bold' });

const Duration = styled(Typography)({
  marginLeft: SPACING_REGULAR,
  cursor: 'pointer',
});

const AvatarWithName = styled(_AvatarWithName)({
  marginLeft: SPACING_UNIT,
});

const InputtedComment = (styled(Typography)({
  wordBreak: 'break-word',
  hyphens: 'auto',
  lineHeight: 1.6,
}) as unknown) as typeof Typography;

const CommentForm = styled(FormControl)({
  width: '100%',
  marginBottom: SPACING_REGULAR,
});

const Comment = styled(TextField)({
  width: '100%',
});

const CommentSummary = styled('div')(
  flexibleLayoutContainer({
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  }),
  {
    marginTop: SPACING_UNIT,
  },
);

const InputCount = styled(FormHelperText)({
  margin: `0 0 0 ${SPACING_UNIT}px`,
  textAlign: 'right',
});

const ButtonArea = styled('div')(
  flexibleLayoutContainer({
    justifyContent: 'flex-end',
  }),
  {
    marginBottom: SPACING_REGULAR,
  },
);

const Submit = styled(Button)({
  marginLeft: SPACING_REGULAR,
});

export default Item;
